import React, { useEffect, useState, useReducer } from 'react'
import moment from 'moment-timezone'
import message from 'antd/lib/message'

import {
  CategoryAndVisibilityStep,
  DateAndTimeStep,
  FormatStep,
  InfoStep,
  TicketSalesStep,
} from './steps'
import { apiManager, constants, openNotificationWithMsg } from 'helpers'
import useAuthState from 'hooks/useAuthState'
import { SET_TAG } from './constants'
import cls from './EventForm.module.scss'

const {
  EVENT_CONSTANTS: { NON_ADMIN_START_WEEKS },
} = constants

const initialState = {
  formats: [],
  categories: [],
  ageGroups: [],
  locations: [],
  sexualOrientation: [],
  subCategoriesByCategory: {},
  miscellaneousLoaded: false,
}

const reducer = (state, action) => ({
  ...state,
  ...action,
})

const EventForm = ({
  formState,
  setFormState,
  handleChange,
  createEvent,
  updateEvent,
  deleteEvent,
  formType,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialState)
  const [errors, setErrors] = useState({})
  const { user } = useAuthState()
  const isAdmin = user.admin

  const submitButtonText = formType === 'create' ? 'Submit For Approval' : 'Update Event'

  const createMessage = errorList => {
    return errorList.reduce((acc, error) => {
      switch (error) {
        case 'title':
          return acc.concat('The field Event Name is required \n')
        case 'imageData':
          return acc.concat('The field Event Image is required \n')
        case 'verticalImageData':
          return acc.concat('The field Vertical Image is required \n')
        case 'description':
          return acc.concat('The field Event Description is required \n')
        case 'startTime':
          return isAdmin
            ? acc.concat("Start Time can't be in the past \n")
            : acc.concat('Start Time must be at least two weeks ahead of current time \n')
        case 'slug':
          return acc.concat('Slug can only have letters, numbers, hyphen (-) and underscore (_) \n')
        case 'age':
          return acc.concat('Min age can not be less than max age')
        default:
          return ''
      }
    }, '')
  }

  const validateFields = () => {
    const requiredFields = ['title', 'description']

    const validationErrors = {}
    requiredFields.forEach(fieldName => {
      if (!formState[fieldName]) {
        validationErrors[fieldName] = true
      }
    })

    if (!isAdmin) {
      if (!formState.imageData) validationErrors.imageData = true
      if (!formState.verticalImageData) validationErrors.verticalImageData = true
    }

    if (
      (isAdmin && formState.startTime.isBefore(moment())) ||
      (!isAdmin &&
        formState.startTime.isBefore(moment().add(NON_ADMIN_START_WEEKS, 'week').startOf('day')))
    ) {
      validationErrors.startTime = true
    }

    if (formState.slug && !/^[a-zA-Z0-9-_]+$/.test(formState.slug)) {
      validationErrors.slug = true
    }

    if (formState.minAge > formState.maxAge) {
      validationErrors.age = true
    }

    const errorsKeys = Object.keys(validationErrors)
    if (errorsKeys.length > 0) {
      setErrors(validationErrors)
      const errorMessage = createMessage(errorsKeys)
      openNotificationWithMsg('error', errorMessage, null, cls.notificationMessage)
      setIsSubmitting(false)
      return false
    }
    return true
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)

    if (validateFields()) {
      const isCreate = formType === 'create'
      try {
        if (isCreate) {
          await createEvent()
        } else {
          await updateEvent()
        }
      } catch (err) {
        message.error(err.message)
        setIsSubmitting(false)
      }
    }
  }

  useEffect(() => {
    setErrors({})
  }, [setErrors, formState])

  useEffect(() => {
    async function loadStaticData() {
      try {
        const miscellaneous = await apiManager.getEventMiscellaneous()
        dispatch({ ...miscellaneous, miscellaneousLoaded: true })
      } catch (err) {
        console.error(err.toString())
      }
    }
    loadStaticData()
  }, [])

  if (!user || !state.miscellaneousLoaded) {
    return null
  }

  return (
    <form className={cls.form} onSubmit={handleSubmit}>
      <InfoStep formState={formState} setFormState={setFormState} errors={errors} />
      <FormatStep
        formState={formState}
        setFormState={setFormState}
        handleChange={handleChange}
        hostLevel={user.Host.level}
        formats={state.formats}
        ageGroups={state.ageGroups}
        sexualOrientation={state.sexualOrientation}
        isAdmin={isAdmin}
      />
      <CategoryAndVisibilityStep
        formState={formState}
        setFormState={setFormState}
        admin={isAdmin}
        categories={state.categories}
        subCategoriesByCategory={state.subCategoriesByCategory}
      />
      <DateAndTimeStep
        formState={formState}
        setFormState={setFormState}
        adminMode={isAdmin}
        errors={errors}
        locations={state.locations}
      />
      <TicketSalesStep
        formState={formState}
        setFormState={setFormState}
        handleChange={handleChange}
        admin={isAdmin}
      />
      <div className={cls.buttonContainer}>
        <button
          data-cy="save-event-btn"
          className={cls.submitButton}
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? 'Submitting...' : submitButtonText}
        </button>
        {formType === 'update' && (
          <button data-cy="delete-event-btn" className={cls.deleteButton} onClick={deleteEvent}>
            Delete Event
          </button>
        )}
      </div>
      <div className={cls.reviewText}>
        Events go through a 24 hour review process to ensure they meet our community guidelines. We
        will let you know when your event is approved.
      </div>
    </form>
  )
}

export default EventForm
export { SET_TAG }
